import { useRef, useState, type ReactNode } from "react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { Loader2 } from "lucide-react";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import type { InputProps } from "ra-core";
import { useIsMobile } from "@/hooks/use-mobile";
import type { PopoverProps } from "@radix-ui/react-popover";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { CrmDataProvider } from "../providers/types";
// GAVION company-create
import {
  COMPANY_SEARCH_FILTER,
  CompanyCreateError,
  cleanCompanyName,
  companyLabel,
  type CompanyCandidate,
} from "../../gavion/companies/companyCreate";

// GAVION company-create: the search (public.search_companies: name, city, phone…), not the companies_summary ilike
const companySearchQuery = (searchText: string) => ({
  [COMPANY_SEARCH_FILTER]: searchText,
});

// GAVION company-create: « Name — City » so the branches of a franchise can be told apart (the create item keeps
// its label)
const companyOptionText = (r?: {
  name?: ReactNode;
  city?: string | null;
  region?: string | null;
}) =>
  r && (r.city || r.region) && typeof r.name === "string"
    ? companyLabel({ name: r.name, city: r.city, region: r.region })
    : r?.name;

/** The rep closed the « Plusieurs entreprises » dialog: no company, no error toast. */
class ChoiceCancelled extends Error {}

type Choice = {
  name: string;
  candidates: CompanyCandidate[];
  resolve: (picked: CompanyCandidate | "create") => void;
  reject: (e: Error) => void;
};

export const AutocompleteCompanyInput = ({
  validate,
  label,
  modal,
}: Pick<InputProps, "validate" | "label"> & Pick<PopoverProps, "modal">) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const translate = useTranslate();
  // GAVION company-create: one create at a time. The ref closes the gap before the re-render disables the item.
  const pendingRef = useRef(false);
  const [pendingName, setPendingName] = useState<string | null>(null);
  const [choice, setChoice] = useState<Choice | null>(null);

  const handleCreateCompany = async (name?: string) => {
    const clean = cleanCompanyName(name ?? "");
    if (!clean) return;
    if (pendingRef.current) {
      // A second click / Enter while the first create is in flight: no request. Resolving with a record would
      // toggle the selection off, resolving empty would select the create item; the first call selects the company.
      return new Promise<never>(() => undefined);
    }
    pendingRef.current = true;
    setPendingName(clean);
    try {
      // GAVION company-create: the same name (case, accents, spacing ignored) reuses the existing company
      let result = await dataProvider.findOrCreateCompany(clean);
      if (result.candidates) {
        // several companies with that name (franchise branches): the rep picks one, or « Créer quand même »
        const candidates = result.candidates;
        const picked = await new Promise<CompanyCandidate | "create">(
          (resolve, reject) =>
            setChoice({ name: clean, candidates, resolve, reject }),
        );
        setChoice(null);
        if (picked !== "create") return picked;
        result = await dataProvider.findOrCreateCompany(clean, true);
        if (result.candidates) throw new Error("unexpected candidates");
      }
      if (!result.created)
        notify("gavion.company_create.existing", {
          type: "info",
          messageArgs: { name: companyLabel(result) },
        });
      return {
        id: result.id,
        name: result.name,
        city: result.city,
        region: result.region,
      };
    } catch (error) {
      if (!(error instanceof ChoiceCancelled))
        notify(
          error instanceof CompanyCreateError && error.code === "invalid_name"
            ? "gavion.company_create.invalid_name"
            : "gavion.company_create.error",
          { type: "error" },
        );
      throw error;
    } finally {
      pendingRef.current = false;
      setPendingName(null);
      setChoice(null);
    }
  };
  const isMobile = useIsMobile();

  return (
    <>
      <AutocompleteInput
        label={label}
        optionText={companyOptionText}
        helperText={false}
        filterToQuery={companySearchQuery}
        onCreate={handleCreateCompany}
        createPending={pendingName !== null}
        createItemLabel={(filter: string) =>
          pendingName !== null ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {translate("gavion.company_create.pending", {
                item: pendingName,
              })}
            </span>
          ) : (
            translate("resources.companies.autocomplete.create_item", {
              item: filter,
            })
          )
        }
        createLabel="resources.companies.autocomplete.create_label"
        validate={validate}
        modal={modal ?? isMobile}
      />
      {/* GAVION company-create: several companies share the typed name */}
      <Dialog
        open={choice !== null}
        onOpenChange={(open) => {
          if (!open) choice?.reject(new ChoiceCancelled());
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate("gavion.company_create.choose.title", {
                name: choice?.name ?? "",
              })}
            </DialogTitle>
            <DialogDescription>
              {translate("gavion.company_create.choose.description")}
            </DialogDescription>
          </DialogHeader>
          <ul className="flex flex-col gap-2">
            {choice?.candidates.map((c) => (
              <li key={String(c.id)}>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => choice.resolve(c)}
                >
                  {companyLabel(c)}
                </Button>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => choice?.reject(new ChoiceCancelled())}
            >
              {translate("gavion.company_create.choose.cancel")}
            </Button>
            <Button type="button" onClick={() => choice?.resolve("create")}>
              {translate("gavion.company_create.choose.create_anyway")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
